<script setup lang="ts">
import { VIcon } from "@app/core";
import { computed } from "vue";

import type { QuickSearchItem } from "../api/quickSearch.gql";

const { item, query, active, index } = defineProps<{
  item: QuickSearchItem
  query: string
  active: boolean
  index: number
}>();

const emit = defineEmits<{
  select: [item: QuickSearchItem]
  "update:active": [index: number]
}>();

function highlight(text: string): string {
  if (!query.trim()) return text;
  const escaped = query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(new RegExp(`(${escaped})`, "gi"), "<mark>$1</mark>");
}

const highlightedName = computed(() => highlight(item.name));
const highlightedSku = computed(() => (item.sku ? highlight(item.sku) : null));
// Only set when the query matched an identifier (e.g. ASIN) — it explains why a product
// whose name and SKU do not contain the query is in the results.
const highlightedIdentifier = computed(() => (item.matchedIdentifier ? highlight(item.matchedIdentifier) : null));
</script>

<template>
  <button
    :class="{ 'result-item--active': active }"
    :style="{ '--stagger-delay': `${index * 30}ms` }"
    class="result-item"
    type="button"
    @click="emit('select', item)"
    @mouseenter="emit('update:active', index)"
  >
    <div class="result-item__thumb">
      <img
        v-if="item.thumbnailUrl"
        :alt="item.name"
        :src="item.thumbnailUrl"
        class="w-full h-full object-cover rounded-md"
      >
      <VIcon
        v-else
        class="text-secondaryText"
        icon="lucide:package"
        :size="20"
      />
    </div>

    <div class="result-item__body">
      <span
        class="result-item__name"
        v-html="highlightedName"
      />
      <span class="result-item__meta">
        <span
          v-if="highlightedSku"
          class="font-mono"
          v-html="highlightedSku"
        />
        <span
          v-if="highlightedSku && item.brandName"
          class="result-item__dot"
        >·</span>
        <span v-if="item.brandName">{{ item.brandName }}</span>
        <span
          v-if="highlightedIdentifier && (highlightedSku || item.brandName)"
          class="result-item__dot"
        >·</span>
        <span
          v-if="highlightedIdentifier"
          class="font-mono"
          v-html="highlightedIdentifier"
        />
      </span>
    </div>
  </button>
</template>

<style scoped>
.result-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 100ms ease;
  animation: result-in 200ms cubic-bezier(0.23, 1, 0.32, 1) both;
  animation-delay: var(--stagger-delay, 0ms);
}

.result-item:hover {
  background-color: rgb(var(--color-primary) / 0.06);
}

.result-item--active {
  background-color: rgb(var(--color-primary) / 0.08);
}

.result-item__thumb {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.375rem;
  background-color: rgb(var(--color-base-200, 241 245 249) / 1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.result-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}

.result-item__name {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--color-mainText, 30 41 59) / 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.result-item__meta {
  font-size: 0.75rem;
  color: rgb(var(--color-secondaryText, 100 116 139) / 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.result-item__dot {
  opacity: 0.5;
}

:deep(mark) {
  background: transparent;
  color: rgb(var(--color-primary, 14 165 233) / 1);
  font-weight: 600;
}

@keyframes result-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .result-item {
    animation: none;
  }
}
</style>
